/**
 * ProductCatalogTab — sección de catálogo dentro de la ficha del producto:
 * fichas/adjuntos (specs, manuales) y compatibilidades (relaciones tipadas
 * producto↔producto). Solo se muestra para productos ya creados.
 */
import { useMemo, useRef, useState } from 'react'
import {
  Paperclip, Upload, Trash2, Loader2, FileText, Link2, Plus, ArrowRight,
} from 'lucide-react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { useToast } from '@/components/Toast'
import { useProducts } from '@/modules/sales/hooks/useSales'
import {
  useProductAttachments, useUploadProductAttachment, useDeleteProductAttachment,
  useProductRelationships, useCreateProductRelationship, useDeleteProductRelationship,
  RELATIONSHIP_TYPES, type RelationshipType,
} from '@/modules/sales/hooks/useCatalog'

const MAX_BYTES = 20 * 1024 * 1024

function humanSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function ProductCatalogTab({ productId, readOnly }: { productId: string; readOnly?: boolean }) {
  const toast = useToast()

  // ── Fichas / adjuntos ──
  const { data: attData, isLoading: attLoading } = useProductAttachments(productId)
  const uploadMut = useUploadProductAttachment(productId)
  const deleteAttMut = useDeleteProductAttachment(productId)
  const fileRef = useRef<HTMLInputElement>(null)
  const usedBytes = attData?.total_size ?? 0

  const handleFile = (file: File | undefined) => {
    if (!file) return
    if (usedBytes + file.size > MAX_BYTES) {
      toast.error('Se superaría el límite de 20 MB por producto.')
      return
    }
    uploadMut.mutate({ file, title: file.name }, {
      onSuccess: () => toast.success('Archivo adjuntado.'),
      onError: () => toast.error('No se pudo adjuntar (tipo permitido: JPG, PNG, WebP, PDF).'),
    })
    if (fileRef.current) fileRef.current.value = ''
  }

  // ── Compatibilidades ──
  const { data: relData, isLoading: relLoading } = useProductRelationships({ product: productId })
  const relationships = relData?.relationships ?? []
  const createRelMut = useCreateProductRelationship()
  const deleteRelMut = useDeleteProductRelationship()

  const { data: prodData } = useProducts({ active: 'true', page_size: 1000 })
  const productOptions = useMemo(
    () => (prodData?.products ?? [])
      .filter(p => p.id !== productId)
      .map(p => ({ id: p.id, label: p.sku ? `${p.name} · ${p.sku}` : p.name })),
    [prodData, productId],
  )

  const [targetId, setTargetId] = useState('')
  const [relType, setRelType] = useState<RelationshipType>('compatible_with')

  const addRelationship = () => {
    if (!targetId) { toast.error('Seleccionar un producto.'); return }
    createRelMut.mutate(
      { from_product: productId, to_product: targetId, relationship_type: relType },
      {
        onSuccess: () => { setTargetId(''); toast.success('Compatibilidad agregada.') },
        onError: () => toast.error('No se pudo agregar (¿relación duplicada?).'),
      },
    )
  }

  return (
    <div className="space-y-5 pt-2">
      {/* Fichas / adjuntos */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider flex items-center gap-1.5">
            <Paperclip size={12} />Fichas y adjuntos
          </p>
          <span className="text-[11px] text-content-subtle">{humanSize(usedBytes)} / 20 MB</span>
        </div>

        {attLoading ? (
          <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-content-subtle" /></div>
        ) : (attData?.attachments.length ?? 0) === 0 ? (
          <p className="text-xs text-content-subtle py-2">Sin archivos. Adjunta specs o manuales (JPG, PNG, WebP, PDF).</p>
        ) : (
          <ul className="space-y-1.5">
            {attData!.attachments.map(att => (
              <li key={att.id} className="flex items-center gap-2 p-2 rounded-lg border border-divider bg-surface">
                <FileText size={14} className="text-primary shrink-0" />
                <a href={att.download_url} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-content hover:text-primary truncate flex-1" title={att.file_name}>
                  {att.title || att.file_name}
                </a>
                <span className="text-[11px] text-content-subtle shrink-0">{humanSize(att.file_size)}</span>
                {!readOnly && (
                  <button type="button" onClick={() => deleteAttMut.mutate(att.id)}
                    className="p-1 rounded text-content-subtle hover:text-danger-content-soft transition-colors" title="Eliminar">
                    <Trash2 size={13} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {!readOnly && (
          <div className="mt-2">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploadMut.isPending}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-content border border-divider rounded-lg hover:bg-soft disabled:opacity-60 transition-colors">
              {uploadMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              Adjuntar archivo
            </button>
          </div>
        )}
      </div>

      {/* Compatibilidades */}
      <div>
        <p className="text-xs font-semibold text-content-subtle uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Link2 size={12} />Compatibilidades y relaciones
        </p>

        {relLoading ? (
          <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-content-subtle" /></div>
        ) : relationships.length === 0 ? (
          <p className="text-xs text-content-subtle py-2">Sin relaciones. Vincula vehículos compatibles, equivalencias o sustitutos.</p>
        ) : (
          <ul className="space-y-1.5">
            {relationships.map(rel => {
              const outgoing = rel.from_product === productId
              const otherName = outgoing ? rel.to_product_name : rel.from_product_name
              const otherSku = outgoing ? rel.to_product_sku : rel.from_product_sku
              return (
                <li key={rel.id} className="flex items-center gap-2 p-2 rounded-lg border border-divider bg-surface">
                  <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-primary-50 text-primary-hover shrink-0">
                    {rel.relationship_type_display}
                  </span>
                  <ArrowRight size={12} className="text-content-subtle shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-content">{otherName}</span>
                    {otherSku && <span className="text-xs text-content-subtle font-mono ml-1.5">{otherSku}</span>}
                  </div>
                  {!readOnly && (
                    <button type="button" onClick={() => deleteRelMut.mutate(rel.id)}
                      className="p-1 rounded text-content-subtle hover:text-danger-content-soft transition-colors" title="Quitar relación">
                      <Trash2 size={13} />
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {!readOnly && (
          <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:items-start">
            <select aria-label="Tipo de relación" value={relType} onChange={e => setRelType(e.target.value as RelationshipType)}
              className="px-3 py-2 border border-divider rounded-lg bg-surface text-content text-sm focus:border-primary focus:ring-1 focus:ring-primary/50 sm:w-44 shrink-0">
              {RELATIONSHIP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <div className="flex-1 min-w-0">
              <EntityAutocomplete aria-label="Producto relacionado"
                options={productOptions}
                value={targetId}
                onChange={setTargetId}
                placeholder="Buscar producto…"
                className="px-3 py-2 border border-divider rounded-lg bg-surface text-content text-sm w-full focus:border-primary focus:ring-1 focus:ring-primary/50"
              />
            </div>
            <button type="button" onClick={addRelationship} disabled={createRelMut.isPending}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors shrink-0">
              {createRelMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Agregar
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
